import { makeAutoObservable } from "mobx";
import { BreadcrumbItem } from "src/appStore";
import {
  loadClassUnsafe,
  updateClass,
  ZClassUnsafe,
} from "src/business/classes";
import { getParentClassId } from "src/business/classes/getParentClassId";
import {
  getSecSpecsFromValues,
  makeClassInternalData,
} from "src/business/classes/makeClassInternalData";
import { makeClassRawData } from "src/business/classes/makeClassRawData";
import { ZSpecification } from "src/business/specification";
import { ZSecSpecRow } from "src/business/specification/ZSecSpecRow";
import { onError } from "src/common/onError";
import { RemoteData } from "src/common/RemoteData";
import { MfErrorsListStore } from "src/components/MForm/components/MfErrorsListView";
import { submitMForm } from "src/components/MForm/submitMForm";
import { ZMfNode } from "src/components/MForm/ZMfNode";
import { msgQueueStore } from "src/components/MsgQueue";
import { PageUrl } from "src/routes/PageUrl";

import { createClassCardFormStore } from "../ClassCardMForm/createClassCardFormStore";
import { updateSecondarySpecs } from "../ClassCardMForm/updateSecondarySpecs";
import { getClassSpecs } from "../getClassSpesc";

export type EditClassData = {
  classData: ZClassUnsafe;
  formMeta: ZMfNode;
  initialData: unknown;
};

const checkClassData = (classData: ZClassUnsafe) => {
  const secSpecNames = new Set<string>();
  classData.secondarySchemas?.find(({ name }) => {
    if (secSpecNames.has(name)) {
      msgQueueStore.add({
        type: "warning",
        message: "Обнаружены некорректные данные!",
        description: `Повторяется использование вторичной спецификации "${name}"`,
      });
      return true;
    }
    secSpecNames.add(name);
    return false;
  });
};

export const editClassPageStore = makeAutoObservable({
  async init(classId: string) {
    try {
      this.setClassId(classId);
      this.setData({ status: "wait" });
      this.errorsStore.clear();
      const classData = await loadClassUnsafe(classId);
      checkClassData(classData);

      const parentClassId = getParentClassId(classData);
      const { meta: formMeta, data: initialData } = makeClassInternalData(
        classData,
        getClassSpecs(classData),
        parentClassId,
      );
      const result = { classData, formMeta, initialData };

      this.setData({ status: "ready", result });
      this.form.initStd(formMeta, initialData, classData.errors);
    } catch (error) {
      this.setData({ status: "error", error });
    }
  },

  /**
   * В результате изменения списка вторичных спецификаций на форме происходит изменение метаданных.
   * Должны исчезнуть или появиться новые разделы
   */
  onSecondarySpecsChanged(newList: ZSecSpecRow[]) {
    const { data } = this;
    if (data.status === "ready") {
      updateSecondarySpecs(data.result.classData, this.form, newList).catch(
        onError,
      );
    }
  },

  errorsStore: new MfErrorsListStore(createClassCardFormStore()),
  get form() {
    return this.errorsStore.form;
  },
  classId: "",
  setClassId(id: string) {
    this.classId = id;
  },
  data: { status: "none" } as RemoteData<EditClassData>,
  setData(newData: RemoteData<EditClassData>) {
    this.data = newData;
  },
  get breadcrumbs(): BreadcrumbItem[] {
    const { data } = this;
    if (data.status !== "ready") return [];
    const { classifier, name } = data.result.classData;
    const res: BreadcrumbItem[] = [
      { title: "Классификаторы", page: PageUrl.classifiers },
    ];
    if (classifier) {
      res.push({
        title: classifier.label,
        page: PageUrl.classes,
        params: { id: classifier.id },
      });
    }
    res.push({ title: name });
    return res;
  },

  mode: null as EditMode,
  setMode(newMode: EditMode) {
    this.mode = newMode;
  },
  async save(): Promise<ZClassUnsafe> {
    const { data, form } = this;
    if (data.status !== "ready") {
      throw Error(`Нет доступа к данным в статусе ${data.status}`);
    }

    const internalValues = form.fieldsValue;
    const allSpecs = [
      data.result.classData.primarySchema,
      ...getSecSpecsFromValues(internalValues),
    ].filter(Boolean) as ZSpecification[];

    const values = makeClassRawData(internalValues, allSpecs);
    const newClassData = await submitMForm(
      () => updateClass(this.classId, values),
      this.errorsStore,
    );

    const firstHierarchy = newClassData.parents?.[0];
    const parentClassId = firstHierarchy?.[0]?.id;

    const { data: result, meta: formMeta } = makeClassInternalData(
      newClassData,
      getClassSpecs(newClassData),
      parentClassId ?? "",
    );

    this.setData({
      status: "ready",
      result: {
        ...data.result,
        formMeta,
        initialData: result,
        classData: newClassData,
      },
    });
    form.setMeta(formMeta);
    form.setFieldsValue(result);
    form.onChange();

    return newClassData;
  },
});

type EditMode = "delete" | null;
